import { atom, getDefaultStore, useAtomValue, useSetAtom } from "jotai"
import { atomFamily } from "jotai-family"
import { useCallback } from "react"

type NodesHighlighted = {
  groupId: string
  nodesId: string[]
}

export enum AddNodeMode {
  None,
  CursorNode,
  CursorGroup,
}

const nodeHighlightedFamily = atomFamily((_id: string) => atom<boolean>(false))

// tracks currently highlighted ids so we can clear them on next set
const highlightedIdsAtom = atom<string[]>([])

export const useIsNodeHighlighted = (id: string) => useAtomValue(nodeHighlightedFamily(id))

export const useSetNodesHighlighted = () => {
  const setHighlightedIds = useSetAtom(highlightedIdsAtom)

  return useCallback(
    ({ groupId, nodesId }: NodesHighlighted) => {
      const store = getDefaultStore()
      const prev = store.get(highlightedIdsAtom)
      for (const id of prev) {
        store.set(nodeHighlightedFamily(id), false)
      }
      const next = groupId ? [groupId, ...nodesId] : nodesId
      for (const id of next) {
        store.set(nodeHighlightedFamily(id), true)
      }
      setHighlightedIds(next)
    },
    [setHighlightedIds],
  )
}

type GraphState = {
  graphId: string | null
  isLocked: boolean
  // Graph can be locked "locally" or "remotely" by another user.
  // If locked remotely, the graph cannot be unlocked by the current user.
  remotelyLockedBy: string | null
  isRemotelyLockedBySomeoneElse: boolean
}

export const addNodeModeAtom = atom<AddNodeMode>(AddNodeMode.None)
export const nextDrawerNodeIdAtom = atom<string | null>(null)
export const nodeFormHasUnsavedChangesAtom = atom<boolean>(false)
export const isClosingNodeDrawerInvokedAtom = atom<boolean>(false)
export const graphAtom = atom<GraphState>({
  graphId: null,
  isLocked: true,
  remotelyLockedBy: null,
  isRemotelyLockedBySomeoneElse: false,
})

export const useGraph = () => useAtomValue(graphAtom)

if (process.env.NODE_ENV !== "production") {
  highlightedIdsAtom.debugLabel = "highlightedIdsAtom"
  addNodeModeAtom.debugLabel = "addNodeModeAtom"
  nextDrawerNodeIdAtom.debugLabel = "nextDrawerNodeIdAtom"
  isClosingNodeDrawerInvokedAtom.debugLabel = "isClosingNodeDrawerInvokedAtom"
  graphAtom.debugLabel = "graphAtom"
  nodeFormHasUnsavedChangesAtom.debugLabel = "nodeFormHasUnsavedChangesAtom"
}
